import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { MapPinLine, CheckCircle } from '@phosphor-icons/react';
import AppIcon from '../components/AppIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELDS = [
  ['address_line1', 'Street address', 'address-line1', true],
  ['address_line2', 'Apt, suite, unit (optional)', 'address-line2', false],
  ['city', 'City', 'address-level2', true],
  ['state', 'State / region', 'address-level1', true],
  ['postal_code', 'ZIP / postal code', 'postal-code', true],
  ['country', 'Country', 'country', true],
];

const Input = ({ name, label, auto, required, value, onChange }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wide text-[#6A6A6A]">{label}</span>
    <input data-testid={`address-form-${name}`} name={name} autoComplete={auto} required={required} value={value} onChange={onChange}
      className="mt-1 w-full px-4 py-3.5 rounded-xl border border-[#EFE3D8] bg-white text-base focus:outline-none focus:border-[#7B2C3B]" />
  </label>
);

export default function AddressForm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({});
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/address-form/${token}`).then(({ data: d }) => {
      setData(d); setForm({ ...d.address, country: d.address.country || 'US' });
    }).catch((e) => setErr(e?.response?.data?.detail || 'This link is not valid.'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      await axios.put(`${API}/address-form/${token}`, { ...form, ...(consent ? { smsConsent: true } : {}) });
      setDone(true);
    } catch (e2) { setErr(typeof e2?.response?.data?.detail === 'string' ? e2.response.data.detail : 'Please check every field and try again.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF7F2] px-5 pt-10 pb-56 sm:pb-10">
      <div className="max-w-md mx-auto">
        {!data && !err && <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#7B2C3B]" /></div>}
        {err && !data && <p data-testid="address-form-error" className="text-center text-[#7B2C3B] py-20">{err}</p>}
        {data && done && (
          <div data-testid="address-form-success" className="text-center py-16">
            <AppIcon icon={CheckCircle} tone="sage" size="lg" reveal className="mx-auto" />
            <h1 className="font-serif text-3xl mt-5 text-[#2A2A2A]">Thank you, {data.guestName.split(' ')[0]}!</h1>
            <p className="text-[#6A6A6A] mt-2">{data.couple} have your address. You can come back to this link anytime to update it.</p>
            <button data-testid="address-form-edit-again" onClick={() => setDone(false)} className="mt-6 text-sm text-[#7B2C3B] underline">Edit my address</button>
          </div>
        )}
        {data && !done && (
          <form onSubmit={submit} data-testid="address-form" className="space-y-4">
            <div className="text-center mb-6">
              <AppIcon icon={MapPinLine} tone="rose" size="lg" reveal className="mx-auto" />
              <p className="section-eyebrow mt-5">{data.couple}</p>
              <h1 className="font-serif text-3xl mt-2 text-[#2A2A2A]">Where should we mail it, {data.guestName.split(' ')[0]}?</h1>
              <p className="text-sm text-[#6A6A6A] mt-2">{data.received ? 'We have your address. Update it below if anything changed.' : 'Takes under a minute. Only the couple sees it.'}</p>
            </div>
            {FIELDS.map(([name, label, auto, required]) => (
              <Input key={name} name={name} label={label} auto={auto} required={required} value={form[name] || ''} onChange={(e) => setForm({ ...form, [name]: e.target.value })} />
            ))}
            {data.hasPhone && data.smsConsentStatus !== 'opted_in' && (
              <label className="flex items-start gap-3 text-xs text-[#4A4A4A] p-3 rounded-xl border border-[#EFE3D8] bg-white">
                <input data-testid="address-form-sms-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 w-5 h-5 accent-[#7B2C3B]" />
                <span>{data.smsConsentText}</span>
              </label>
            )}
            {err && <p data-testid="address-form-submit-error" className="text-sm text-[#7B2C3B]">{err}</p>}
            <button data-testid="address-form-submit" disabled={busy} className="w-full py-4 rounded-full bg-[#7B2C3B] text-[#FBF7F2] text-base font-medium active:scale-[0.98] transition-transform disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save my address
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
